import { useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select } from "../components/ui/Fields";
import { Badge } from "../components/ui/Primitives";
import { ConfirmDialog } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 45 follow-up, item 13 — "admin grants granular permissions
 * (power/admin, host-anywhere, ban) to a user by public ID, never
 * automatically all permissions to every admin."
 *
 * Over routes/admin/user-permissions.js. Same lookup-then-form shape as
 * Send Medal / Coin Adjustments: search by the user's PUBLIC User ID,
 * see their current permission set, edit it as independent checkboxes,
 * confirm, then PATCH saves EXACTLY that set (replace, not merge) —
 * every save is a complete, explicit statement of the intended set,
 * never a series of easy-to-forget individual toggles.
 */
type PermKey = "power_admin" | "host_anywhere" | "ban" | "room_settings" | "ghost_mode" | "admin_entry_mode";
const PERMISSIONS: { key: PermKey; label: string; description: string }[] = [
  {
    key: "power_admin",
    label: "Power Admin",
    description: "Elevated standing flag, shown on this user's profile/account.",
  },
  {
    key: "host_anywhere",
    label: "Host Anywhere",
    description: "Allowed to host/go live without the normal room-host prerequisites.",
  },
  {
    key: "ban",
    label: "Moderation (Ban)",
    description:
      "Grants the in-app Mute / Ban Chat / Ban Account actions from Profile Info's 3-dot menu. The one permission with real enforcement teeth — grant carefully.",
  },
  {
    key: "room_settings",
    label: "Allow access to User Room Settings",
    description:
      "Lets this App Admin open and change any room's Room Settings (and its Blocked List) from inside the room. Enforced by the server on every request; without it the controls are hidden and the requests are refused.",
  },
  {
    key: "ghost_mode",
    label: "Ghost Mode",
    description:
      "Adds a Ghost Mode ON/OFF switch to this App Admin's app Settings. While ON they are invisible in rooms (no entry message, not counted, not listed, a seat looks empty). Moderation powers are unchanged. Revoking this switches Ghost Mode off immediately.",
  },
  {
    key: "admin_entry_mode",
    label: "Admin Entry Mode",
    description:
      "Adds an Admin Entry Mode ON/OFF switch to this App Admin's app Settings. Turning it ON makes the animation configured below play full-screen in every live room. Needs an animation URL saved below to take effect; the server authorises every toggle. Revoking this switches it OFF.",
  },
];

interface AdminRow {
  userId: number;
  username: string;
  avatarUrl: string | null;
  role: string;
  active: boolean;
  permissions: string[];
}

interface EntryConfig {
  animType: "svga" | "mp4";
  animationUrl: string;
  floatingButtonAllowed: boolean;
}

const PERM_LABEL: Record<string, string> = Object.fromEntries(PERMISSIONS.map((p) => [p.key, p.label]));

interface FoundUser {
  id: string; // internal UUID — targets the PATCH call
  user_id: number; // public ID
  username: string;
  avatar_url: string | null;
  permissions: Record<PermKey, boolean>;
}

const EMPTY_PERMS: Record<PermKey, boolean> = {
  power_admin: false,
  host_anywhere: false,
  ban: false,
  room_settings: false,
  ghost_mode: false,
  admin_entry_mode: false,
};

export function UserPermissionsPage() {
  const [searchId, setSearchId] = useState("");
  const [found, setFound] = useState<FoundUser | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const [admins, setAdmins] = useState<AdminRow[] | null>(null);
  const [adminsError, setAdminsError] = useState<string | null>(null);
  const [entryCfg, setEntryCfg] = useState<EntryConfig>({ animType: "svga", animationUrl: "", floatingButtonAllowed: false });
  const [isSavingCfg, setIsSavingCfg] = useState(false);

  async function loadAdmins() {
    try {
      setAdmins(await apiRequest<AdminRow[]>("/admin/user-permissions/admins"));
      setAdminsError(null);
    } catch (err) {
      setAdminsError(err instanceof ApiError ? err.message : "Could not load admins");
    }
  }
  useEffect(() => {
    loadAdmins();
  }, []);

  async function loadEntryConfig(publicId: number) {
    try {
      setEntryCfg(await apiRequest<EntryConfig>(`/admin/user-permissions/entry-config/${publicId}`));
    } catch {
      setEntryCfg({ animType: "svga", animationUrl: "", floatingButtonAllowed: false });
    }
  }

  async function saveEntryCfg() {
    if (!found) return;
    setIsSavingCfg(true);
    try {
      await apiRequest(`/admin/user-permissions/entry-config/${found.user_id}`, { method: "PATCH", body: entryCfg });
      showToast("Admin Entry Mode configuration saved", "success");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save configuration", "error");
    } finally {
      setIsSavingCfg(false);
    }
  }

  async function handleSearch(idOverride?: string) {
    const query = (idOverride ?? searchId).trim();
    if (!query) return;
    setIsSearching(true);
    setSearchError(null);
    setFound(null);
    try {
      const user = await apiRequest<FoundUser>("/admin/user-permissions/lookup", {
        params: { query },
      });
      setFound(user);
      loadEntryConfig(user.user_id);
      const granted = new Set<string>();
      for (const p of PERMISSIONS) if (user.permissions[p.key]) granted.add(p.key);
      setSelected(granted);
    } catch (err) {
      setSearchError(err instanceof ApiError ? err.message : "Lookup failed");
    } finally {
      setIsSearching(false);
    }
  }

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function hasChanges(): boolean {
    if (!found) return false;
    const current = new Set<string>();
    for (const p of PERMISSIONS) if (found.permissions[p.key]) current.add(p.key);
    if (current.size !== selected.size) return true;
    for (const p of current) if (!selected.has(p)) return true;
    return false;
  }

  async function handleConfirm() {
    if (!found) return;
    setIsSubmitting(true);
    try {
      const permissions = Array.from(selected);
      const result = await apiRequest<{ ok: boolean; permissions: string[] }>(
        `/admin/user-permissions/${found.id}`,
        { method: "PATCH", body: { permissions } }
      );
      showToast(
        result.permissions.length
          ? `Saved — ${found.username} now holds: ${result.permissions.join(", ")}`
          : `Saved — ${found.username} now holds no special permissions`,
        "success"
      );
      setFound((f) =>
        f
          ? {
              ...f,
              permissions: {
                ...EMPTY_PERMS,
                ...Object.fromEntries(permissions.map((k) => [k, true])),
              },
            }
          : f
      );
      setIsConfirmOpen(false);
      loadAdmins();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save permissions", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AppShell title="User Permissions">
      <p className="mb-4 max-w-xl text-xs text-ink-faint">
        Grant or revoke a user's permissions individually — saving always sets the user's permission set to exactly
        what's checked below, so nothing is ever granted by default or left over from a previous grant. The Ban
        permission lets a regular user issue Mute / Ban Chat / Ban Account against other users from inside the app;
        every such action is still re-checked and recorded server-side regardless of what the app's own menu shows.
      </p>

      <Card className="max-w-xl p-5">
        <div className="flex items-end gap-2">
          <Input
            label="Find user by public User ID"
            placeholder="e.g. 74848506"
            value={searchId}
            onChange={(e) => {
              setSearchId(e.target.value);
              setFound(null);
              setSearchError(null);
            }}
          />
          <Button variant="secondary" onClick={() => handleSearch()} isLoading={isSearching} disabled={!searchId.trim()}>
            Find
          </Button>
        </div>
        {searchError && <p className="mt-1.5 text-xs text-danger">{searchError}</p>}

        {found && (
          <div className="mt-4 space-y-4 border-t border-surface-border pt-4">
            <div className="flex items-center gap-3">
              <Avatar src={found.avatar_url} className="h-10 w-10 rounded-full object-cover" />
              <div>
                <p className="font-semibold text-ink">{found.username}</p>
                <p className="font-mono text-xs text-ink-faint">ID: {found.user_id}</p>
              </div>
            </div>

            <div className="space-y-3">
              {PERMISSIONS.map((perm) => (
                <label
                  key={perm.key}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-surface-border p-3 hover:bg-surface-raised"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(perm.key)}
                    onChange={() => toggle(perm.key)}
                    className="mt-0.5 h-4 w-4 rounded border-surface-border"
                  />
                  <div>
                    <p className="text-sm font-semibold text-ink">{perm.label}</p>
                    <p className="text-xs text-ink-faint">{perm.description}</p>
                  </div>
                </label>
              ))}
            </div>

            <Button variant="primary" onClick={() => setIsConfirmOpen(true)} disabled={!hasChanges()}>
              Save Permissions
            </Button>

            {found.permissions.admin_entry_mode && (
              <div className="space-y-3 rounded-lg border border-surface-border p-3">
                <p className="text-sm font-semibold text-ink">Admin Entry Mode — configuration</p>
                <Select
                  label="Entry animation type"
                  value={entryCfg.animType}
                  onChange={(e) => setEntryCfg((c) => ({ ...c, animType: e.target.value as "svga" | "mp4" }))}
                >
                  <option value="svga">SVGA</option>
                  <option value="mp4">MP4</option>
                </Select>
                <Input
                  label="Animation URL (https)"
                  value={entryCfg.animationUrl}
                  onChange={(e) => setEntryCfg((c) => ({ ...c, animationUrl: e.target.value }))}
                />
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={entryCfg.floatingButtonAllowed}
                    onChange={(e) => setEntryCfg((c) => ({ ...c, floatingButtonAllowed: e.target.checked }))}
                  />
                  Allow Floating Button (draggable button inside rooms)
                </label>
                <Button variant="secondary" onClick={saveEntryCfg} isLoading={isSavingCfg} disabled={!entryCfg.animationUrl.trim()}>
                  Save configuration
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="mt-6 p-5">
        <p className="mb-3 text-sm font-semibold text-ink">Admins</p>
        {adminsError && <p className="text-xs text-danger">{adminsError}</p>}
        {!admins && !adminsError && <p className="text-xs text-ink-faint">Loading…</p>}
        {admins && admins.length === 0 && <p className="text-xs text-ink-faint">No admins hold any permission yet.</p>}
        {admins && admins.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-ink-faint">
                <tr>
                  <th className="py-2 pr-3">User ID</th>
                  <th className="py-2 pr-3">Username</th>
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3">Permissions</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.userId} className="border-t border-surface-border">
                    <td className="py-2 pr-3 font-mono text-xs">{a.userId}</td>
                    <td className="py-2 pr-3 font-medium text-ink">{a.username}</td>
                    <td className="py-2 pr-3">{a.role}</td>
                    <td className="py-2 pr-3 text-xs text-ink-faint">
                      {a.permissions.map((k) => PERM_LABEL[k] ?? k).join(", ") || "—"}
                    </td>
                    <td className="py-2 pr-3">
                      {a.active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}
                    </td>
                    <td className="py-2 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setSearchId(String(a.userId));
                          handleSearch(String(a.userId));
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Manage
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => !isSubmitting && setIsConfirmOpen(false)}
        onConfirm={handleConfirm}
        isLoading={isSubmitting}
        isDangerous={selected.has("ban") && !found?.permissions.ban}
        title="Confirm permission changes"
        confirmLabel="Save"
        description={
          found
            ? `Set ${found.username} (ID: ${found.user_id})'s permissions to exactly: ${
                selected.size ? Array.from(selected).join(", ") : "none"
              }.`
            : ""
        }
      />
    </AppShell>
  );
}
